import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * PlayCircleOutline component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPlayCircleOutline extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60547 12C2.60547 6.81127 6.81175 2.60498 12.0005 2.60498C17.1892 2.60498 21.3955 6.81127 21.3955 12C21.3955 17.1887 17.1892 21.395 12.0005 21.395C6.81175 21.395 2.60547 17.1887 2.60547 12ZM12.0005 1.10498C5.98333 1.10498 1.10547 5.98284 1.10547 12C1.10547 18.0171 5.98333 22.895 12.0005 22.895C18.0176 22.895 22.8955 18.0171 22.8955 12C22.8955 5.98284 18.0176 1.10498 12.0005 1.10498ZM10.0569 7.46215C9.82572 7.31765 9.53431 7.30999 9.29585 7.44216C9.05738 7.57433 8.90942 7.8255 8.90942 8.09815V15.902C8.90942 16.1746 9.05738 16.4258 9.29585 16.558C9.53431 16.6901 9.82572 16.6825 10.0569 16.538L16.3 12.6361C16.5193 12.499 16.6525 12.2587 16.6525 12.0001C16.6525 11.7415 16.5193 11.5011 16.3 11.3641L10.0569 7.46215ZM14.4874 12.0001L10.4094 14.5488V9.45133L14.4874 12.0001Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-play-circle-outline') === undefined) {
  customElements.define('cat-icon-play-circle-outline', BlocksIconPlayCircleOutline);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-play-circle-outline': BlocksIconPlayCircleOutline;
  }
}
